// Copyright (c) 2026 Query Health Inc. All rights reserved.
// Author: Anmol Rajpal <anmol@quantinohealth.com>
// Licensed under a Commercial License. See LICENSE for terms.

import { auth } from '@/lib/firebase'
import type { OrgDetail, OrgStatus, OrgSummary, OnboardOrgRequest, UsageResponse } from '@/types'

// query-health-api base URL. Empty by default → same-origin relative paths
// (the Vite dev proxy / prod ingress routes /admin to the API). Set
// VITE_API_BASE_URL to point at a different API host.
const API_BASE = (import.meta.env.VITE_API_BASE_URL ?? '').replace(/\/$/, '')

// PHI path prefixes that the console MUST NEVER call — enforced at runtime.
// The console only talks to /admin/* endpoints; any attempt to call a patient
// data path is a programming error that we catch before reaching the network.
const PHI_PREFIXES = ['/v1/patients', '/v1/notes']

// Structured API error so callers can branch on status + code.
export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly code: string,
    message?: string,
  ) {
    super(message ?? `API error ${status}: ${code}`)
    this.name = 'ApiError'
  }
}

// Low-level fetch helper: resolves API base, attaches X-Request-ID and Bearer.
export async function apiFetch(input: string, init: RequestInit = {}): Promise<Response> {
  const url = /^https?:\/\//.test(input) ? input : `${API_BASE}${input}`

  const headers = new Headers(init.headers)
  headers.set('X-Request-ID', crypto.randomUUID())

  const user = auth.currentUser
  if (user) {
    const token = await user.getIdToken()
    headers.set('Authorization', `Bearer ${token}`)
  }

  return fetch(url, { ...init, headers })
}

// Internal request wrapper — enforces the PHI invariant, handles non-ok
// responses, and parses JSON. Exported so tests can probe it directly.
export async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  // Refuse any call to a PHI path before touching the network.
  for (const prefix of PHI_PREFIXES) {
    if (path.startsWith(prefix)) {
      throw new ApiError(
        0,
        'phi_route_forbidden',
        `Console must not access PHI path: ${path}`,
      )
    }
  }

  const res = await apiFetch(path, init)

  if (!res.ok) {
    let code = 'unknown'
    try {
      const body = (await res.json()) as { error?: string }
      if (body.error) code = body.error
    } catch {
      // body not JSON — keep 'unknown'
    }
    throw new ApiError(res.status, code, `API ${res.status}: ${code}`)
  }

  if (res.status === 204) {
    return undefined as T
  }

  return res.json() as Promise<T>
}

// ── Typed wrappers ─────────────────────────────────────────────────────────────

export async function listOrgs(): Promise<OrgSummary[]> {
  const body = await request<{ orgs: OrgSummary[] }>('/admin/orgs')
  return body.orgs
}

export async function getOrg(orgId: string): Promise<OrgDetail> {
  return request<OrgDetail>(`/admin/orgs/${encodeURIComponent(orgId)}`)
}

export async function createOrg(body: OnboardOrgRequest): Promise<OrgDetail> {
  return request<OrgDetail>('/admin/orgs', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  })
}

// Deliberate design: backend planned PATCH /admin/orgs/{id} with {status} for
// suspend/reactivate. The frontend spec suggested POST :suspend but we follow
// the backend's documented REST shape (PATCH + status field). See report.
export async function setOrgStatus(orgId: string, status: OrgStatus): Promise<OrgDetail> {
  return request<OrgDetail>(`/admin/orgs/${encodeURIComponent(orgId)}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ status }),
  })
}

export async function suspendOrg(orgId: string): Promise<OrgDetail> {
  return setOrgStatus(orgId, 'suspended')
}

export async function reactivateOrg(orgId: string): Promise<OrgDetail> {
  return setOrgStatus(orgId, 'active')
}

export async function getUsage(): Promise<UsageResponse> {
  return request<UsageResponse>('/admin/usage')
}

// ── Operator team membership (operator-org scoped; keyed by stable member id) ──

// MemberResponse returned by the /v1/org/members endpoints. The console manages
// its own operator org — no X-Org-Context is sent (apiFetch never adds it).
export interface OrgMember {
  id: string
  email: string
  fullName?: string
  role: string
  status: string
  uid?: string
}

/** List the operator org's members (invited + active). */
export async function listMembers(): Promise<OrgMember[]> {
  const body = await request<{ members: OrgMember[] }>('/v1/org/members')
  return body.members
}

/** Invite an operator teammate by email + role. 409 = already a member. */
export function inviteMember(input: { email: string; role: string }): Promise<OrgMember> {
  return request<OrgMember>('/v1/org/members', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  })
}

/** Re-send the SSO invite email + refresh the sign-in window (pending members). */
export function resendInvite(id: string): Promise<OrgMember> {
  return request<OrgMember>(`/v1/org/members/${encodeURIComponent(id)}/resend`, { method: 'POST' })
}

/** Change a member's role (within the caller's grantable set). */
export function changeMemberRole(id: string, role: string): Promise<OrgMember> {
  return request<OrgMember>(`/v1/org/members/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ role }),
  })
}

/** Remove a member — cancels a pending invite or offboards an active one. Idempotent. */
export function removeMember(id: string): Promise<OrgMember> {
  return request<OrgMember>(`/v1/org/members/${encodeURIComponent(id)}`, { method: 'DELETE' })
}

// ── Auth bootstrap ─────────────────────────────────────────────────────────────

// Returned shape from POST /v1/auth/bootstrap.
export interface BootstrapResult { orgId: string; role: string }

// Binds the signed-in Firebase UID to the operator's membership record and
// stamps {org_id, role} custom claims. Idempotent — safe to call on every
// first-login where claims are absent. The /v1/auth/* prefix is intentionally
// excluded from PHI_PREFIXES so this call is never blocked.
export function bootstrapSession(): Promise<BootstrapResult> {
  return request<BootstrapResult>('/v1/auth/bootstrap', { method: 'POST' })
}
